// Per-listener podcast playback sessions (tier 1 of the sync degradation ladder).
//
// The server fetches a fresh publisher rendition for this listener, fingerprints
// it against the episode's pinned reference copy, and re-times the transcript to
// it. That work is asynchronous, so the client creates a session and then polls
// until it either lands (`retimed`) or gives up. Every failure path is a no-op
// for the page: it simply keeps playing tier 2 (the pinned copy) or tier 3 (the
// no-sync banner), both of which already work.
//
// Deliberately dependency-free — no imports, one `export default` — so
// test/javascript/playback_session_test.js can load it with plain Node and no
// node_modules, the way audio_sync_test.js loads AudioSync.

const DEFAULT_POLL_INTERVAL_MS = 3000;
const DEFAULT_MAX_ATTEMPTS = 60;

// Statuses the API contract defines. `pending` and `fetched` mean "keep polling";
// everything else is terminal.
const PENDING_STATUSES = ['pending', 'fetched'];

class PlaybackSession {
  // `scheduler` and `fetch` are injectable purely so tests can drive the state
  // machine without real timers or a network.
  constructor(options = {}) {
    this.fetchImpl = options.fetch || null;
    this.scheduler = options.scheduler || null;
    this.pollIntervalMs = options.pollIntervalMs || DEFAULT_POLL_INTERVAL_MS;
    this.maxAttempts = options.maxAttempts || DEFAULT_MAX_ATTEMPTS;

    this.eventHandlers = {};
    this.sessionId = null;
    this.status = 'idle';
    this.attempts = 0;
    this.timerId = null;
    this.finished = false;
  }

  // Register an event handler. Mirrors the Transcript class's tiny emitter.
  // Events: 'retimed' ({ audioUrl, transcriptUrl }), 'failed' ({ reason }).
  on(event, handler) {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event].push(handler);
    return this;
  }

  trigger(event, ...args) {
    if (this.eventHandlers[event]) {
      this.eventHandlers[event].forEach(handler => handler(...args));
    }
  }

  // Create the session, then poll it. Resolves once the first response has been
  // handled; the terminal outcome arrives through the callbacks.
  start(episodeId, csrfToken, authToken) {
    if (!episodeId) {
      return this.fail('missing_episode_id');
    }

    this.episodeId = episodeId;
    this.csrfToken = csrfToken;
    this.authToken = authToken;
    this.status = 'creating';

    const url = `/api/v1/podcast_episodes/${encodeURIComponent(episodeId)}/playback_session`;

    return this.request('POST', url)
      .then(data => this.handleResponse(data))
      .catch(error => this.fail('request_failed', error));
  }

  // Idempotent — safe to call on an already-finished session, and called by
  // video.js when a turbolinks navigation replaces the page.
  stop() {
    this.finished = true;

    if (this.timerId !== null) {
      this.resolveScheduler().clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  poll() {
    if (this.finished) {
      return Promise.resolve();
    }

    this.attempts += 1;

    const url = `/api/v1/podcast_playback_sessions/${encodeURIComponent(this.sessionId)}`;

    return this.request('GET', url)
      .then(data => this.handleResponse(data))
      .catch(error => this.fail('request_failed', error));
  }

  handleResponse(data) {
    if (this.finished) {
      return;
    }

    if (!data || typeof data !== 'object') {
      return this.fail('invalid_response');
    }

    if (data.session_id) {
      this.sessionId = data.session_id;
    }

    this.status = data.status;

    if (data.status === 'retimed') {
      if (!data.transcript_url) {
        // Retimed without a transcript is not usable; tier 2 already works.
        return this.fail('missing_transcript_url');
      }

      this.finish();
      this.trigger('retimed', {
        sessionId: this.sessionId,
        audioUrl: data.audio_url,
        transcriptUrl: data.transcript_url
      });
      return;
    }

    if (PENDING_STATUSES.indexOf(data.status) === -1) {
      // 'failed', 'unavailable' (podcast not in the pilot allowlist, or no
      // pinned reference), or anything unrecognised.
      return this.fail(data.status || 'unknown_status');
    }

    if (!this.sessionId) {
      return this.fail('missing_session_id');
    }

    if (this.attempts >= this.maxAttempts) {
      return this.fail('timeout');
    }

    this.schedulePoll();
  }

  schedulePoll() {
    this.timerId = this.resolveScheduler().setTimeout(() => this.poll(), this.pollIntervalMs);
  }

  fail(reason, error) {
    if (this.finished) {
      return;
    }

    this.finish();
    this.status = 'failed';
    this.trigger('failed', { reason, error });
  }

  finish() {
    this.stop();
  }

  request(method, url) {
    const fetchFn = this.resolveFetch();

    if (!fetchFn) {
      return Promise.reject(new Error('fetch is unavailable'));
    }

    return fetchFn(url, {
      method,
      headers: this.buildHeaders(),
      credentials: 'same-origin'
    }).then(response => {
      if (!response || !response.ok) {
        // Includes the throttle responses (in-flight cap, daily cap, global
        // cap) — all of which mean "stay on tier 2".
        const error = new Error(`playback session request failed: ${response && response.status}`);
        error.status = response && response.status;
        throw error;
      }

      return response.json();
    });
  }

  buildHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };

    if (this.csrfToken) {
      headers['X-CSRF-Token'] = this.csrfToken;
    }

    // api/v1 authenticates with a bearer token; the view injects one for the
    // signed-in user the same way the chat page does.
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  // Resolved lazily so a stub installed after construction still applies.
  resolveFetch() {
    if (this.fetchImpl) {
      return this.fetchImpl;
    }

    return typeof fetch === 'function' ? (...args) => fetch(...args) : null;
  }

  resolveScheduler() {
    if (this.scheduler) {
      return this.scheduler;
    }

    return {
      setTimeout: (handler, delay) => setTimeout(handler, delay),
      clearTimeout: timerId => clearTimeout(timerId)
    };
  }
}

export default PlaybackSession;
